import { useId } from 'react';
import type { ReadingBlock } from '../types.ts';

/** Authored teaching blocks; no Markdown parsing, HTML injection, or remote assets. */
export function LessonBlocks({ blocks }: { blocks: ReadingBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => (
        <LessonBlock key={index} block={block} />
      ))}
    </>
  );
}

function LessonBlock({ block }: { block: ReadingBlock }) {
  switch (block.kind) {
    case 'paragraph':
      return <p>{block.text}</p>;
    case 'equation':
      return (
        <figure className="lesson-equation">
          <figcaption>{block.label}</figcaption>
          {block.lines.map((line, i) => (
            <div className="equation-step" key={i}>
              <div
                className="math-scroll"
                tabIndex={0}
                role="region"
                aria-label={`${block.label}, equation ${i + 1}`}
              >
                <math display="block">
                  <mrow>
                    <mtext>{line.left}</mtext>
                    <mo>=</mo>
                    {typeof line.right === 'string' ? (
                      <mtext>{line.right}</mtext>
                    ) : (
                      <mfrac>
                        <mtext>{line.right.numerator}</mtext>
                        <mtext>{line.right.denominator}</mtext>
                      </mfrac>
                    )}
                  </mrow>
                </math>
              </div>
              {line.reason && <p className="equation-reason">{line.reason}</p>}
            </div>
          ))}
          {block.note && <p className="equation-note">{block.note}</p>}
        </figure>
      );
    case 'example':
      return (
        <section className="worked-example" aria-label={block.title}>
          <h3>{block.title}</h3>
          <LessonBlocks blocks={block.blocks} />
        </section>
      );
    case 'table':
      return (
        <div className="lesson-table-scroll" tabIndex={0} role="region" aria-label={block.caption}>
          <table className="lesson-table">
            <caption>{block.caption}</caption>
            <thead>
              <tr>
                {block.headers.map((cell, i) => (
                  <th scope="col" key={i}>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) =>
                    j === 0 ? (
                      <th scope="row" key={j}>
                        {cell}
                      </th>
                    ) : (
                      <td key={j}>{cell}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'diagram':
      return <LessonDiagram block={block} />;
  }
}

function LessonDiagram({ block }: { block: Extract<ReadingBlock, { kind: 'diagram' }> }) {
  const id = useId();
  return (
    <figure className="lesson-diagram" tabIndex={0}>
      <svg viewBox="0 0 600 310" role="img" aria-labelledby={`${id}-title ${id}-desc`}>
        <title id={`${id}-title`}>{block.caption}</title>
        <desc id={`${id}-desc`}>{block.description}</desc>
        <defs>
          <marker
            id={`${id}-arrow`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10 Z" fill="currentColor" />
          </marker>
        </defs>
        {['market-demand', 'market-balance', 'market-shift'].includes(block.drawing) && (
          <>
            {/* Invented rental market: Q = 120 - 3P, supply Q = 3P,
                shifted demand Q = 150 - 3P. x = 85 + 3.5Q; y = 255 - 5P. */}
            <path d="M85 45 V255 H520" />
            <text x="18" y="180" transform="rotate(-90 18 180)" textAnchor="middle">
              Price ($ per rental)
            </text>
            <text x="295" y="306" textAnchor="middle">
              Rentals per week
            </text>
            {[0, 30, 60, 90, 120].map((q) => (
              <g key={q}>
                <path d={`M${85 + 3.5 * q} 255 v5`} />
                <text x={85 + 3.5 * q} y="279" textAnchor="middle">
                  {q}
                </text>
              </g>
            ))}
            {[0, 10, 20, 30, 40].map((p) => (
              <g key={p}>
                <path d={`M80 ${255 - 5 * p} h5`} />
                <text x="70" y={260 - 5 * p} textAnchor="end">
                  ${p}
                </text>
              </g>
            ))}
            <path d="M85 155 H295 V255" strokeDasharray="3 4" opacity="0.65" />
            <path d="M85 55 L505 255" />
            {block.drawing !== 'market-shift' && (
              <text x="430" y="210">
                Demand
              </text>
            )}
            {block.drawing === 'market-demand' &&
              [30, 60, 90].map((q) => (
                <circle
                  key={q}
                  cx={85 + 3.5 * q}
                  cy={255 - 5 * ((120 - q) / 3)}
                  r="4"
                  fill="currentColor"
                />
              ))}
            {block.drawing === 'market-balance' && (
              <>
                <path d="M85 255 L505 55" strokeDasharray="8 4" />
                <text x="457" y="55">
                  Supply
                </text>
                <circle cx="295" cy="155" r="5" fill="currentColor" />
                <path d="M301 157 L350 169" opacity="0.65" />
                <text x="360" y="176">
                  Plans match
                </text>
              </>
            )}
            {block.drawing === 'market-shift' && (
              <>
                <text x="116" y="35">
                  Original demand
                </text>
                <text x="309" y="35">
                  Demand with more visitors
                </text>
                <path d="M190 55 L505 205" strokeDasharray="8 4" style={{ color: '#9b6618' }} />
                <path d="M400 155 V255" strokeDasharray="3 4" opacity="0.65" />
                <path d="M302 155 H393" markerEnd={`url(#${id}-arrow)`} />
                <circle cx="295" cy="155" r="4" fill="currentColor" />
                <circle cx="400" cy="155" r="4" fill="#9b6618" />
              </>
            )}
          </>
        )}
        {block.drawing === 'conditional-grid' && (
          <>
            <text x="45" y="28">
              Ten equally likely tickets
            </text>
            {Array.from({ length: 10 }, (_, i) => (
              <g key={i}>
                <rect
                  x={45 + (i % 5) * 55}
                  y={55 + Math.floor(i / 5) * 65}
                  width="43"
                  height="48"
                  rx="0"
                  className={i < 4 ? 'diagram-shade' : 'diagram-empty'}
                />
                <text x={66 + (i % 5) * 55} y={85 + Math.floor(i / 5) * 65} textAnchor="middle">
                  {i < 4 ? 'R' : 'B'}
                  {[0, 1, 4].includes(i) ? '★' : ''}
                </text>
              </g>
            ))}
            <path d="M345 110 H390" markerEnd={`url(#${id}-arrow)`} />
            <text x="465" y="60" textAnchor="middle">
              Keep only starred
            </text>
            <rect x="412" y="80" width="115" height="130" className="diagram-empty" />
            <text x="470" y="115" textAnchor="middle">
              R★ R★
            </text>
            <text x="470" y="155" textAnchor="middle">
              B★
            </text>
            <text x="300" y="260" textAnchor="middle">
              2 red among 3 starred → P(red | star) = 2/3
            </text>
            <text x="45" y="220">
              R = red · B = blue · ★ = starred
            </text>
          </>
        )}
        {block.drawing === 'arrival-area' && (
          <>
            <rect x="70" y="25" width="240" height="240" className="diagram-shade" />
            <path d="M70 25 H230 L70 185 Z M150 265 H310 V105 Z" className="diagram-empty" />
            <path d="M70 185 L230 25 M150 265 L310 105" strokeDasharray="5 4" />
            <path d="M70 15 V265 H325" />
            <text x="65" y="286">
              0
            </text>
            <text x="145" y="286">
              20
            </text>
            <text x="292" y="286">
              60
            </text>
            <text x="38" y="32">
              60
            </text>
            <text x="38" y="190">
              20
            </text>
            <text x="178" y="307">
              First arrival (minutes)
            </text>
            <text x="18" y="230" transform="rotate(-90 18 230)">
              Second arrival (minutes)
            </text>
            <rect x="355" y="55" width="18" height="18" className="diagram-shade" />
            <text x="385" y="69">
              Visits overlap
            </text>
            <text x="355" y="115">
              Shaded band: |X − Y| ≤ 20
            </text>
            <text x="355" y="155">
              Each unshaded triangle
            </text>
            <text x="355" y="180">
              has legs of 40 minutes.
            </text>
            <text x="355" y="225">
              Area represents probability
            </text>
            <text x="355" y="249">
              because arrivals are uniform
            </text>
            <text x="355" y="273">
              and independent.
            </text>
          </>
        )}
        {block.drawing === 'coin-states' && (
          <>
            <rect x="25" y="110" width="145" height="62" className="diagram-empty" />
            <text x="97" y="137" textAnchor="middle">
              No trailing head
            </text>
            <text x="97" y="158" textAnchor="middle">
              E₀
            </text>
            <rect x="245" y="110" width="135" height="62" className="diagram-empty" />
            <text x="312" y="137" textAnchor="middle">
              One trailing head
            </text>
            <text x="312" y="158" textAnchor="middle">
              E₁
            </text>
            <rect x="465" y="110" width="100" height="62" className="diagram-shade" />
            <text x="515" y="137" textAnchor="middle">
              HH: done
            </text>
            <text x="515" y="158" textAnchor="middle">
              0 remaining
            </text>
            <path d="M170 130 H242 M380 130 H462" markerEnd={`url(#${id}-arrow)`} />
            <text x="204" y="114" textAnchor="middle">
              H · ½
            </text>
            <text x="422" y="114" textAnchor="middle">
              H · ½
            </text>
            <path d="M312 173 V220 H98 V175" markerEnd={`url(#${id}-arrow)`} />
            <text x="205" y="243" textAnchor="middle">
              T · ½: restart
            </text>
            <path d="M120 108 C175 30 25 30 75 106" markerEnd={`url(#${id}-arrow)`} />
            <text x="95" y="35" textAnchor="middle">
              T · ½
            </text>
            <text x="300" y="286" textAnchor="middle">
              Every arrow costs one toss, including the arrow into “done.”
            </text>
          </>
        )}
        {block.drawing === 'net-force' && (
          <>
            <rect x="235" y="100" width="110" height="90" className="diagram-shade" />
            <text x="290" y="150" textAnchor="middle">
              2 kg
            </text>
            <path d="M235 145 H115 M345 145 H545" markerEnd={`url(#${id}-arrow)`} />
            <text x="160" y="120">
              6 N left
            </text>
            <text x="410" y="120">
              10 N right
            </text>
            <path d="M250 245 H330" markerEnd={`url(#${id}-arrow)`} />
            <text x="300" y="280" textAnchor="middle">
              Net: 10 − 6 = 4 N right → a = 4 / 2 = 2 m/s² right
            </text>
            <text x="300" y="45" textAnchor="middle">
              Sum forces on the same object, keeping their directions.
            </text>
          </>
        )}
        {block.drawing === 'energy-flow' && (
          <>
            <rect x="35" y="100" width="140" height="85" className="diagram-empty" />
            <text x="105" y="135" textAnchor="middle">
              Energy in
            </text>
            <text x="105" y="162" textAnchor="middle">
              100 J
            </text>
            <rect x="230" y="100" width="140" height="85" className="diagram-shade" />
            <text x="300" y="135" textAnchor="middle">
              System storage
            </text>
            <text x="300" y="162" textAnchor="middle">
              +20 J
            </text>
            <rect x="425" y="100" width="140" height="85" className="diagram-empty" />
            <text x="495" y="135" textAnchor="middle">
              Energy out
            </text>
            <text x="495" y="162" textAnchor="middle">
              80 J
            </text>
            <path d="M175 143 H226 M370 143 H421" markerEnd={`url(#${id}-arrow)`} />
            <text x="300" y="245" textAnchor="middle">
              Over the same interval: change in storage = inputs − outputs.
            </text>
          </>
        )}
        {block.drawing === 'cone-similarity' && (
          <>
            <path d="M50 30 L160 250 L270 30 Z" />
            <path d="M132.5 195 L160 250 L187.5 195 Z" className="diagram-shade" />
            <path d="M160 30 V250" strokeDasharray="5 4" />
            <text x="215" y="18" textAnchor="middle">
              Radius R
            </text>
            <path
              d="M160 24 H270"
              markerStart={`url(#${id}-arrow)`}
              markerEnd={`url(#${id}-arrow)`}
            />
            <path
              d="M35 30 V250"
              markerStart={`url(#${id}-arrow)`}
              markerEnd={`url(#${id}-arrow)`}
            />
            <text x="17" y="145">
              H
            </text>
            <text x="196" y="224">
              h = H/4
            </text>
            <text x="315" y="80">
              A vertical slice through the axis
            </text>
            <text x="315" y="120">
              Radius fraction = 1/4
            </text>
            <text x="315" y="153">
              Base-area fraction = (1/4)²
            </text>
            <text x="315" y="186">
              Volume fraction = (1/4)³ = 1/64
            </text>
            <text x="300" y="292" textAnchor="middle">
              Height and radius shrink together; volume changes with their cube.
            </text>
          </>
        )}
        {block.drawing === 'coordinate-distance' && (
          <>
            <path d="M35 285 H310 M50 300 V15" markerEnd={`url(#${id}-arrow)`} />
            <path d="M90 205 H210 V45 Z" className="diagram-shade" />
            <path d="M195 205 V190 H210" />
            <text x="75" y="228">
              A (1,2)
            </text>
            <text x="205" y="34">
              B (4,6)
            </text>
            <text x="130" y="225">
              3 units
            </text>
            <text x="220" y="135">
              4 units
            </text>
            <text x="102" y="125">
              5 units
            </text>
            <text x="360" y="105">
              Straight route: √(3² + 4²) = 5
            </text>
            <text x="360" y="145">
              Grid route: 3 + 4 = 7
            </text>
            <text x="300" y="301" textAnchor="middle">
              Horizontal and vertical coordinate changes form perpendicular legs.
            </text>
          </>
        )}
        {block.drawing === 'circuit-paths' && (
          <>
            <text x="145" y="28" textAnchor="middle">
              Series: one route
            </text>
            <path d="M20 140 H70 M120 140 H170 M220 140 H270" />
            <rect x="70" y="130" width="50" height="20" className="diagram-empty" />
            <rect x="170" y="130" width="50" height="20" className="diagram-empty" />
            <text x="95" y="115" textAnchor="middle">
              6 Ω
            </text>
            <text x="195" y="115" textAnchor="middle">
              6 Ω
            </text>
            <text x="145" y="190" textAnchor="middle">
              Same current through both
            </text>
            <text x="145" y="218" textAnchor="middle">
              12 V across the pair → 1 A
            </text>
            <text x="455" y="28" textAnchor="middle">
              Parallel: two routes
            </text>
            <path d="M330 140 H350 V75 H430 M480 75 H560 V205 H480 M430 205 H350 V140 M560 140 H580" />
            <rect x="430" y="65" width="50" height="20" className="diagram-empty" />
            <rect x="430" y="195" width="50" height="20" className="diagram-empty" />
            <text x="455" y="57" textAnchor="middle">
              6 Ω
            </text>
            <text x="455" y="190" textAnchor="middle">
              6 Ω
            </text>
            <text x="455" y="125" textAnchor="middle">
              Same voltage
            </text>
            <text x="455" y="150" textAnchor="middle">
              across both
            </text>
            <text x="455" y="242" textAnchor="middle">
              12 V → 2 A per branch
            </text>
            <text x="300" y="289" textAnchor="middle">
              Each network connects across an ideal 12 V source at its two endpoints.
            </text>
          </>
        )}
        {block.drawing === 'domino-cases' && (
          <>
            <text x="145" y="28" textAnchor="middle">
              Left edge: one vertical domino
            </text>
            <rect x="25" y="65" width="250" height="100" className="diagram-empty" />
            <rect x="25" y="65" width="50" height="100" className="diagram-shade" />
            <text x="165" y="120" textAnchor="middle">
              Remaining board
            </text>
            <text x="145" y="205" textAnchor="middle">
              n − 1 columns remain
            </text>
            <text x="455" y="28" textAnchor="middle">
              Left edge: two horizontal dominoes
            </text>
            <rect x="325" y="65" width="250" height="100" className="diagram-empty" />
            <rect x="325" y="65" width="100" height="50" className="diagram-shade" />
            <rect x="325" y="115" width="100" height="50" className="diagram-shade" />
            <text x="495" y="110" textAnchor="middle">
              Remaining
            </text>
            <text x="495" y="135" textAnchor="middle">
              board
            </text>
            <text x="455" y="205" textAnchor="middle">
              n − 2 columns remain
            </text>
            <text x="300" y="266" textAnchor="middle">
              These cases cover every tiling and cannot overlap.
            </text>
            <text x="300" y="295" textAnchor="middle">
              T(n) = T(n − 1) + T(n − 2)
            </text>
          </>
        )}
        {block.drawing === 'wave-frequency' && (
          <>
            <text x="45" y="25">
              Same propagation speed; snapshots across the same distance
            </text>
            {[
              { y: 95, period: 250, label: 'Longer wavelength, lower frequency' },
              { y: 225, period: 125, label: 'Shorter wavelength, higher frequency' },
            ].map(({ y, period, label }) => (
              <g key={period}>
                <path d={`M50 ${y} H550`} strokeDasharray="4 4" />
                <path
                  d={Array.from(
                    { length: 201 },
                    (_, i) =>
                      `${i ? 'L' : 'M'}${50 + i * 2.5} ${y - 30 * Math.sin((i * 2.5 * 2 * Math.PI) / period)}`,
                  ).join(' ')}
                />
                <text x="50" y={y - 48}>
                  {label}
                </text>
                <path
                  d={`M50 ${y + 40} h${period}`}
                  markerStart={`url(#${id}-arrow)`}
                  markerEnd={`url(#${id}-arrow)`}
                />
                <text x={50 + period / 2} y={y + 60} textAnchor="middle">
                  One wavelength
                </text>
              </g>
            ))}
          </>
        )}

        {block.drawing === 'ribbon-cut' && (
          <>
            <text x="40" y="35">
              A one-meter ribbon, with two mirror-image cuts
            </text>
            {[
              { y: 80, cut: 156, left: '0.3 m', right: '0.7 m' },
              { y: 200, cut: 364, left: '0.7 m', right: '0.3 m' },
            ].map(({ y, cut, left, right }) => (
              <g key={y}>
                <rect x="40" y={y} width="520" height="40" className="diagram-empty" />
                <rect
                  x={left === '0.7 m' ? 40 : 40 + cut}
                  y={y}
                  width="364"
                  height="40"
                  className="diagram-shade"
                />
                <path d={`M${40 + cut} ${y - 15} V${y + 55}`} />
                <text x={40 + cut / 2} y={y + 80} textAnchor="middle">
                  {left}
                </text>
                <text x={40 + cut + (520 - cut) / 2} y={y + 80} textAnchor="middle">
                  {right}
                </text>
              </g>
            ))}
          </>
        )}
        {block.drawing === 'plant-transport' && (
          <>
            <text x="100" y="30" textAnchor="middle">
              Xylem · water
            </text>
            <text x="420" y="30" textAnchor="middle">
              Phloem · sugar
            </text>
            <rect x="40" y="70" width="120" height="45" className="diagram-empty" />
            <text x="100" y="98" textAnchor="middle">
              Leaves
            </text>
            <rect x="40" y="235" width="120" height="45" className="diagram-empty" />
            <text x="100" y="263" textAnchor="middle">
              Roots
            </text>
            <path d="M100 230 V122" markerEnd={`url(#${id}-arrow)`} />
            <text x="125" y="180">
              Water rises
            </text>
            <rect x="340" y="135" width="170" height="45" className="diagram-shade" />
            <text x="425" y="163" textAnchor="middle">
              Source leaf
            </text>
            <rect x="340" y="55" width="170" height="45" className="diagram-empty" />
            <text x="425" y="83" textAnchor="middle">
              Growing shoot
            </text>
            <rect x="340" y="235" width="170" height="45" className="diagram-empty" />
            <text x="425" y="263" textAnchor="middle">
              Storage root
            </text>
            <path d="M425 130 V105" markerEnd={`url(#${id}-arrow)`} />
            <path d="M425 185 V230" markerEnd={`url(#${id}-arrow)`} />
          </>
        )}
      </svg>
      <figcaption>{block.caption}</figcaption>
    </figure>
  );
}
